/**
 * Date-range options for the access log. Resolved client-side into a UTC window and sent to the
 * server — see `resolveDateWindow()`.
 */
export type AccessLogDateRange = 'today' | 'yesterday' | 'd7' | 'd30' | 'd90' | 'm12';

/** Type options. `export` covers both the server's Download and Transmit event types. */
export type AccessLogTypeFilter = 'all' | 'view' | 'print' | 'export';

/**
 * The view's filter state. Every field is sent to the API and applied server-side (PBI 56315 AC3) —
 * there is deliberately no client-side filtering of loaded rows, which would only ever filter the
 * pages already fetched.
 */
export interface AccessLogFilters {
  dateRange: AccessLogDateRange;
  type: AccessLogTypeFilter;
  /** `null` is "All". Otherwise a `patientPortalUserId` from the actor pick list. */
  actorId: number | null;
  searchTerm: string;
}

/** Last 30 Days is the default range, per the approved wireframe. */
export const DEFAULT_DATE_RANGE: AccessLogDateRange = 'd30';

/**
 * The longest range offered, and what AC6's "Show last 12 months" empty-state action widens to. Named
 * so the widen affordance can hide itself once there is nothing left to widen to, without a literal.
 */
export const WIDEST_DATE_RANGE: AccessLogDateRange = 'm12';

export const DEFAULT_ACCESS_LOG_FILTERS: AccessLogFilters = {
  dateRange: DEFAULT_DATE_RANGE,
  type: 'all',
  actorId: null,
  searchTerm: '',
};

export const DATE_RANGE_OPTIONS: ReadonlyArray<{ key: AccessLogDateRange; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'd7', label: 'Last 7 Days' },
  { key: 'd30', label: 'Last 30 Days' },
  { key: 'd90', label: 'Last 90 Days' },
  { key: 'm12', label: 'Last 12 Months' },
];

export const TYPE_OPTIONS: ReadonlyArray<{ key: AccessLogTypeFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'view', label: 'Viewed' },
  { key: 'print', label: 'Printed' },
  { key: 'export', label: 'Exported' },
];
